import React from 'react'
import { get } from 'lodash'
import { EVENT_MODULES } from 'lib/utils/store/wrappers/event/modules'
import moment from 'moment'
import { isDev } from 'lib/env'

import CompactFormGroup from 'components/Forms/CompactForm/CompactFormGroup/CompactFormGroup'
import EventFormTitleModule from './EventFormModules/EventFormTitleModule/EventFormTitleModule'
import EventFormEventTimeModule from './EventFormModules/EventFormEventTimeModule/EventFormEventTimeModule'
import EventFormCommentModule from './EventFormModules/EventFormCommentModule/EventFormCommentModule'
import EventFormAttachmentsModule from './EventFormModules/EventFormAttachmentsModule/EventFormAttachmentsModule'
import EventFormMileageModule from './EventFormModules/EventFormMileageModule/EventFormMileageModule'
import EventFormExpenseModule from './EventFormModules/EventFormExpenseModule/EventFormExpenseModule'
import EventFormIncomeModule from './EventFormModules/EventFormIncomeModule/EventFormIncomeModule'
import EventFormInspectionResultModule from './EventFormModules/EventFormInspectionResultModule/EventFormInspectionResultModule'
import EventFormInspectionNoticesModule from './EventFormModules/EventFormInspectionNoticesModule/EventFormInspectionNoticesModule'
import EventFormServiceTypeModule from './EventFormModules/EventFormServiceTypeModule/EventFormServiceTypeModule'
import EventFormLocationModule from './EventFormModules/EventFormLocationModule/EventFormLocationModule'
import EventFormReminderModule from './EventFormModules/EventFormReminderModule/EventFormReminderModule'
import EventFormRemindNextModule from './EventFormModules/EventFormRemindNextModule/EventFormRemindNextModule'
import EventFormRefillModule from './EventFormModules/EventFormRefillModule/EventFormRefillModule'
import EventFormInspectionTrafiDataModule from './EventFormModules/EventFormInspectionTrafiDataModule/EventFormInspectionTrafiDataModule'
import EventFormTrafiEventTimeModule from './EventFormModules/EventFormTrafiEventTimeModule/EventFormTrafiEventTimeModule'

const useEventFormModulesBuilder = ({
    form,
    wrappedEvent,
    t,
}) => {
    const eventInPast = !moment(form.values.eventTime).isAfter(moment(Date.now()))
    const formMode = form.values.formMode

    const builderProps = {
        formMode,
        form,
        wrappedEvent,
        eventInPast,
    }

    const validateFormInitialValue = (module) => {
        // ignore value validation for modules that are not using "form.values"
        // attachments are not dependent on formik form as they are handled
        // in own component EventFormAttachmentsLogic.js
        if ([EVENT_MODULES.ATTACHMENTS].includes(module.type)) return true

        const { initialValues } = form

        for (const fieldName of module.fieldNames) {
            if (typeof get(initialValues, fieldName, undefined) === 'undefined') {
                if (isDev()) console.log(initialValues)
                throw new Error(`Module missing from form.initialValues: ${fieldName}`)
            }
        }
    }

    // validate module values
    wrappedEvent.modules.map((module) => validateFormInitialValue(module))

    const renderModule = (module) => {
        switch (module.type) {
        case EVENT_MODULES.TITLE:
            return <EventFormTitleModule key={module.type} label={t('events:forms.title.label')} />
        case EVENT_MODULES.COMMENT:
            return <EventFormCommentModule key={module.type} label={t('events:forms.comment.label')} />
        case EVENT_MODULES.EVENT_TIME:
            return <EventFormEventTimeModule label={t('events:forms.eventTime.label')} name="eventTime" key={module.type} />
        case EVENT_MODULES.TRAFI_EVENT_TIME:
            return <EventFormTrafiEventTimeModule label={t('events:forms.eventTime.label')} name="eventTime" key={module.type} />
        case EVENT_MODULES.REMINDER:
            return <EventFormReminderModule key={module.type} module={module} />
        case EVENT_MODULES.REMIND_NEXT:
            return <EventFormRemindNextModule key={module.type} module={module} />
        case EVENT_MODULES.MILEAGE:
            return <EventFormMileageModule key={module.type} />
        case EVENT_MODULES.ATTACHMENTS:
            return (
                <EventFormAttachmentsModule
                    key={module.type}
                    attachments={form.values.attachments}
                    label={t('events:forms.attachment.label')}
                />
            )
        case EVENT_MODULES.EXPENSE:
            return <EventFormExpenseModule key={module.type} label={t('events:forms.expense.label')} />
        case EVENT_MODULES.INCOME:
            return <EventFormIncomeModule key={module.type} label={t('events:forms.income.label')} />
        case EVENT_MODULES.REFILL:
            return <EventFormRefillModule key={module.type} label={t('events:forms.refill.label')} />
        case EVENT_MODULES.INSPECTION_TRAFI_DATA:
            return <EventFormInspectionTrafiDataModule key={module.type} label={t('events:forms.inspectionResult.label')} />
        case EVENT_MODULES.INSPECTION_RESULT:
            return <EventFormInspectionResultModule key={module.type} label={t('events:forms.inspectionResult.label')} />
        case EVENT_MODULES.INSPECTION_NOTICES:
            return <EventFormInspectionNoticesModule key={module.type} label={t('events:forms.inspectionNotice.label')} />
        case EVENT_MODULES.SERVICE_TYPE:
            return <EventFormServiceTypeModule key={module.type} label={t('events:forms.serviceType.label')} />
        case EVENT_MODULES.LOCATION:
            return <EventFormLocationModule key={module.type} label={t('events:forms.location.label')} />
        default:
            console.error(`Invalid module type: ${module.type}`)
            return null
        }
    }

    const renderModulesWithLayout = (fieldsLayoutSchema, modules) => {
        return fieldsLayoutSchema.layout.map((item, i) => {
            const groupFields = item.modules.reduce((result, type) => {
                const module = modules.find(module => module.type === type)
                if (module && module.shouldRenderInForm && module.shouldRenderInForm(builderProps)) {
                    result.push(module)
                }
                return result
            }, [])

            // const groupFields = modules.filter(module => item.modules.includes(module.type))
            return (groupFields && groupFields.length > 0) && (
                <CompactFormGroup key={i} className={item.className}>
                    {groupFields.map((module) => renderModule(module))}
                </CompactFormGroup>
            )
        })
    }

    return {
        modules: wrappedEvent.modules,
        renderModulesWithLayout,
    }
}

export default useEventFormModulesBuilder
